import type { Data } from "@measured/puck";

// ─────────────────────────────────────────────────────────────────────────────
// ID counter
// ─────────────────────────────────────────────────────────────────────────────

let idCounter = 0;
const uid = (prefix: string) => `${prefix}-${++idCounter}`;
const resetIds = () => {
  idCounter = 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// Low-level style helpers
// ─────────────────────────────────────────────────────────────────────────────

const styleOf = (el: HTMLElement | null, prop: string): string => {
  if (!el?.style) return "";
  return el.style.getPropertyValue(prop) || "";
};

const inheritedStyle = (el: HTMLElement | null, prop: string): string => {
  let cur = el?.parentElement ?? null;
  while (cur && cur.tagName !== "BODY") {
    const v = styleOf(cur, prop);
    if (v) return v;
    if (cur.tagName === "FONT") {
      if (prop === "font-family" && cur.getAttribute("face"))
        return cur.getAttribute("face")!;
      if (prop === "color" && cur.getAttribute("color"))
        return cur.getAttribute("color")!;
    }
    cur = cur.parentElement;
  }
  return "";
};

const ABSTRACT_CSS = new Set([
  "inherit",
  "initial",
  "unset",
  "currentcolor",
  "",
]);
const concreteInheritedStyle = (
  el: HTMLElement | null,
  prop: string,
): string => {
  let cur = el?.parentElement ?? null;
  while (cur && cur.tagName !== "BODY") {
    const v = styleOf(cur, prop);
    if (v && !ABSTRACT_CSS.has(v)) return v;
    if (cur.tagName === "FONT") {
      if (prop === "font-family" && cur.getAttribute("face"))
        return cur.getAttribute("face")!;
      if (prop === "color" && cur.getAttribute("color"))
        return cur.getAttribute("color")!;
    }
    cur = cur.parentElement;
  }
  return "";
};

const visibleText = (t: string): string => t.replace(/[​‌‍﻿ ]/g, "").trim();

const normalizeColorInStyle = (style: string): string =>
  style.replace(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)[^)]*\)/g,
    (_, r, g, b) =>
      "#" +
      [r, g, b].map((n) => parseInt(n).toString(16).padStart(2, "0")).join(""),
  );

const normalizeColor = (raw: string): string => {
  if (!raw) return raw;
  const rgb = raw.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb)
    return (
      "#" +
      [rgb[1], rgb[2], rgb[3]]
        .map((n) => parseInt(n).toString(16).padStart(2, "0"))
        .join("")
    );
  return raw;
};

const toPx = (raw: string): string => {
  if (!raw) return "";
  const m = raw.match(/([\d.]+)\s*(px|pt|em|rem|%)/i);
  if (!m) return raw;
  const v = parseFloat(m[1]);
  const u = m[2].toLowerCase();
  if (u === "pt") return `${Math.round(v * 1.333)}px`;
  if (u === "px") return `${Math.round(v)}px`;
  return raw;
};

const expandPadding = (sh: string) => {
  const p = sh.trim().split(/\s+/);
  if (p.length === 1)
    return {
      top: toPx(p[0]),
      right: toPx(p[0]),
      bottom: toPx(p[0]),
      left: toPx(p[0]),
    };
  if (p.length === 2)
    return {
      top: toPx(p[0]),
      right: toPx(p[1]),
      bottom: toPx(p[0]),
      left: toPx(p[1]),
    };
  if (p.length === 3)
    return {
      top: toPx(p[0]),
      right: toPx(p[1]),
      bottom: toPx(p[2]),
      left: toPx(p[1]),
    };
  return {
    top: toPx(p[0]),
    right: toPx(p[1]),
    bottom: toPx(p[2]),
    left: toPx(p[3]),
  };
};

const elementPadding = (el: HTMLElement) => {
  const t = toPx(styleOf(el, "padding-top"));
  const b = toPx(styleOf(el, "padding-bottom"));
  const l = toPx(styleOf(el, "padding-left"));
  const r = toPx(styleOf(el, "padding-right"));
  const sh = styleOf(el, "padding");
  const exp = sh
    ? expandPadding(sh)
    : { top: "", right: "", bottom: "", left: "" };
  return {
    top: t || exp.top,
    bottom: b || exp.bottom,
    left: l || exp.left,
    right: r || exp.right,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Font normalization
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";

const FONT_MAP: Record<string, string> = {
  "open sans": "open Sans Helvetica, Arial, sans-serif",
  "open sans helvetica": "open Sans Helvetica, Arial, sans-serif",
  arial: "Arial, Helvetica, sans-serif",
  "helvetica neue": "'Helvetica Neue', Helvetica, Arial, sans-serif",
  helvetica: "Helvetica, Arial, sans-serif",
  verdana: "Verdana, Geneva, Tahoma, sans-serif",
  "trebuchet ms": "'Trebuchet MS', Helvetica, sans-serif",
  tahoma: "Tahoma, Geneva, sans-serif",
  "gill sans": "'Gill Sans', 'Gill Sans MT', Calibri, sans-serif",
  "gill sans mt": "'Gill Sans', 'Gill Sans MT', Calibri, sans-serif",
  calibri: "Calibri, Candara, Segoe, 'Segoe UI', Optima, Arial, sans-serif",
  "segoe ui": "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  "lucida sans unicode": "'Lucida Sans Unicode', 'Lucida Grande', sans-serif",
  "lucida grande": "'Lucida Sans Unicode', 'Lucida Grande', sans-serif",
  "century gothic": "'Century Gothic', CenturyGothic, AppleGothic, sans-serif",
  centurygothic: "'Century Gothic', CenturyGothic, AppleGothic, sans-serif",
  "franklin gothic medium":
    "'Franklin Gothic Medium', 'Arial Narrow Bold', Arial, sans-serif",
  "times new roman": "'Times New Roman', Times, serif",
  times: "'Times New Roman', Times, serif",
  georgia: "Georgia, 'Times New Roman', Times, serif",
  garamond: "Garamond, 'Times New Roman', serif",
  "palatino linotype": "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
  palatino: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
  "book antiqua": "'Book Antiqua', Palatino, serif",
  cambria: "Cambria, 'Times New Roman', serif",
  didot: "Didot, 'Didot LT STD', 'Hoefler Text', serif",
  "courier new": "'Courier New', Courier, monospace",
  courier: "'Courier New', Courier, monospace",
  "lucida console": "'Lucida Console', Monaco, monospace",
  monaco: "'Lucida Console', Monaco, monospace",
  consolas: "Consolas, 'Courier New', monospace",
};

const normalizeFont = (raw: string): string => {
  if (!raw) return DEFAULT_FONT;
  for (const part of raw.split(",")) {
    const key = part.trim().replace(/['"]/g, "").toLowerCase();
    if (FONT_MAP[key]) return FONT_MAP[key];
  }
  return DEFAULT_FONT;
};

// ─────────────────────────────────────────────────────────────────────────────
// Size snap helpers
// ─────────────────────────────────────────────────────────────────────────────

const FONT_SIZES = [
  10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 28, 32, 36, 40, 48,
];
const PAD_SIZES = [
  0, 5, 8, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90,
  95, 100,
];

const snapFont = (raw: string): string => {
  const px = parseFloat(toPx(raw));
  if (isNaN(px)) return "13px"; // Font di fallback se non presente
  return `${FONT_SIZES.reduce((p, c) => (Math.abs(c - px) < Math.abs(p - px) ? c : p))}px`;
};

const snapPad = (raw: string, fallback: string): string => {
  const px = parseFloat(toPx(raw));
  if (isNaN(px)) return fallback;
  return `${PAD_SIZES.reduce((p, c) => (Math.abs(c - px) < Math.abs(p - px) ? c : p))}px`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Interlinea
// ─────────────────────────────────────────────────────────────────────────────

const LINE_HEIGHTS = [1, 1.15, 1.2, 1.4, 1.5, 1.6, 1.8, 2, 2.5, 3];
const snapLineHeight = (
  raw: string,
  fontSizePx: number,
  fallback: string,
): string => {
  const v = (raw || "").trim().toLowerCase();
  if (!v || v === "normal") return fallback;
  const m = v.match(/^([\d.]+)\s*(px|pt|em|rem|%)?$/);
  if (!m) return fallback;
  const n = parseFloat(m[1]);
  if (isNaN(n)) return fallback;
  const u = m[2] || "";
  let ratio: number;
  if (!u) ratio = n;
  else if (u === "em" || u === "rem") ratio = n;
  else if (u === "%") ratio = n / 100;
  else if (u === "px") ratio = fontSizePx ? n / fontSizePx : NaN;
  else if (u === "pt") ratio = fontSizePx ? (n * 1.333) / fontSizePx : NaN;
  else ratio = NaN;
  if (isNaN(ratio)) return fallback;
  const snapped = LINE_HEIGHTS.reduce((p, c) =>
    Math.abs(c - ratio) < Math.abs(p - ratio) ? c : p,
  );
  return String(snapped);
};

// ─────────────────────────────────────────────────────────────────────────────
// MJML "content div" resolver
// ─────────────────────────────────────────────────────────────────────────────

const resolveContentDiv = (
  el: HTMLElement,
): {
  contentDiv: HTMLElement | null;
  paddingTd: HTMLElement | null;
} => {
  // Helper: does this element carry real typography (not MJML's font-size:0px layout trick)?
  const hasTypography = (node: HTMLElement) => {
    const ff = styleOf(node, "font-family");
    const fs = styleOf(node, "font-size");
    const hasRealFontSize =
      fs && fs !== "0" && fs !== "0px" && !fs.startsWith("0 ");
    return !!ff || !!hasRealFontSize;
  };

  // If el is already the content source
  if (hasTypography(el)) {
    return {
      contentDiv: el,
      paddingTd:
        el.parentElement?.tagName === "TD"
          ? (el.parentElement as HTMLElement)
          : null,
    };
  }

  // Walk down looking for the first element that carries font-family.
  // We check DIVs first (MJML content div), then fall back to P/A (CTA <p href> case).
  const walkDown = (root: HTMLElement, tags: string[]): HTMLElement | null => {
    for (const node of Array.from(root.querySelectorAll<HTMLElement>("*"))) {
      if (tags.includes(node.tagName) && hasTypography(node)) return node;
    }
    return null;
  };

  const candidate =
    walkDown(el, ["DIV"]) ?? walkDown(el, ["P", "A", "SPAN", "TD"]);
  if (candidate) {
    return {
      contentDiv: candidate,
      paddingTd:
        candidate.parentElement?.tagName === "TD"
          ? (candidate.parentElement as HTMLElement)
          : null,
    };
  }

  return { contentDiv: null, paddingTd: null };
};

// ─────────────────────────────────────────────────────────────────────────────
// Styling props extractor
// ─────────────────────────────────────────────────────────────────────────────

const extractProps = (el: HTMLElement, isMjml: boolean) => {
  // ── Typography ──────────────────────────────────────────────────────────
  let fontFamily = DEFAULT_FONT;
  let fontSize = "13px";
  let color = "#000000";
  let textAlign = "left";
  let fontWeight = "normal";
  let fontStyle = "normal";
  let textDecoration = "none";
  let rawLineHeight = "";

  if (isMjml) {
    const { contentDiv } = resolveContentDiv(el);
    const src = contentDiv ?? el;

    fontFamily = normalizeFont(
      styleOf(src, "font-family") || inheritedStyle(src, "font-family"),
    );
    fontSize = snapFont(
      styleOf(src, "font-size") || inheritedStyle(src, "font-size"),
    );
    {
      const rawColor =
        styleOf(src, "color") || inheritedStyle(src, "color") || "";

      if (rawColor && !ABSTRACT_CSS.has(rawColor)) {
        const innerStyled = Array.from(
          src.querySelectorAll<HTMLElement>("[style]"),
        );
        const NATURAL_TAGS = new Set(["EM", "STRONG", "I", "B", "U"]);
        const naturalNodes = innerStyled.filter((n) =>
          NATURAL_TAGS.has(n.tagName),
        );
        const allNaturalInherit =
          naturalNodes.length > 0 &&
          naturalNodes.every((n) => {
            const c = styleOf(n, "color");
            return !c || ABSTRACT_CSS.has(c);
          });
        const divColorOnInnerSpan = innerStyled.some(
          (n) =>
            n.tagName === "SPAN" &&
            !n.closest("a") &&
            styleOf(n, "color") === rawColor,
        );
        color = allNaturalInherit && divColorOnInnerSpan ? "#000000" : rawColor;
      } else {
        color = "#000000";
      }
    }
    textAlign =
      styleOf(src, "text-align") || inheritedStyle(src, "text-align") || "left";
    rawLineHeight =
      styleOf(src, "line-height") || inheritedStyle(src, "line-height") || "";
    fontWeight = styleOf(src, "font-weight") || "normal";
    fontStyle = styleOf(src, "font-style") || "normal";

    // Normalize weight tokens
    if (["400", "300", "200", "100"].includes(fontWeight))
      fontWeight = "normal";
    if (["700", "600", "800", "900", "bold"].includes(fontWeight))
      fontWeight = "bold";

    // Structural bold/italic/underline inside rich content
    if (src.querySelector("b, strong")) fontWeight = "bold";
    if (src.querySelector("i, em")) fontStyle = "italic";
    if (src.querySelector("u")) textDecoration = "underline";
  } else {
    // SunEditor usa spesso "color: inherit" / "font-size: inherit" sugli elementi
    // intermedi (strong, div) e mette i valori concreti solo sullo span più interno.
    // Usiamo readConcrete() che salta inherit/initial/unset e scende in tutti i
    // discendenti [style] per trovare il primo valore concreto, cadendo infine
    // su inheritedStyle (risalita DOM verso i padri).
    const ABSTRACT = new Set([
      "inherit",
      "initial",
      "unset",
      "currentcolor",
      "",
    ]);
    const styledNodes = Array.from(el.querySelectorAll<HTMLElement>("[style]"));

    // Per font-size, font-family e color preferiamo il valore degli span interni
    // rispetto a quello dell'elemento wrapper. Motivo: SunEditor copia il font del
    // paragrafo Word (es. "Aptos 11pt" / "rgb(36,36,36)") sull'elemento P o TD, mentre
    // i valori reali del testo (es. "Century Gothic 9pt" / "black") stanno sugli span
    // figli. Scansionando prima i discendenti otteniamo il font corretto.
    const PREFER_INNER = new Set(["font-size", "font-family", "color"]);

    const readConcrete = (p: string): string => {
      if (PREFER_INNER.has(p)) {
        if (p === "color") {
          const elColor = styleOf(el, "color");
          if (elColor && !ABSTRACT.has(elColor)) {
            const inheritsDirectlyFromEl = (n: HTMLElement): boolean => {
              let cur = n.parentElement;
              while (cur && cur !== el) {
                const c = styleOf(cur, "color");
                if (c && !ABSTRACT.has(c)) return false;
                cur = cur.parentElement;
              }
              return true;
            };
            const NATURAL = new Set(["EM", "STRONG", "I", "B", "U"]);
            const naturalInheritCount = styledNodes.filter(
              (n) =>
                NATURAL.has(n.tagName) &&
                !n.closest("a") &&
                ABSTRACT.has(styleOf(n, "color") || "") &&
                inheritsDirectlyFromEl(n),
            ).length;
            const concreteSpanCount = styledNodes.filter(
              (n) =>
                n.tagName === "SPAN" &&
                !n.closest("a") &&
                !ABSTRACT.has(styleOf(n, "color") || ""),
            ).length;
            if (naturalInheritCount > concreteSpanCount) return elColor;
          }
        }
        // Scansiona i discendenti in ordine INVERSO (foglia → radice).
        // Le foglie portano il font/color reale del testo; i wrapper più in alto
        // portano metadati strutturali (es. <strong font-size:13px> che avvolge
        // uno <span font-size:16pt> con il titolo reale).
        for (let i = styledNodes.length - 1; i >= 0; i--) {
          const node = styledNodes[i];
          if (p === "color" && node.closest("a")) continue;
          const v = styleOf(node, p);
          if (!ABSTRACT.has(v)) return v;
        }
      }
      // Poi l'elemento stesso
      const direct = styleOf(el, p);
      if (!ABSTRACT.has(direct)) return direct;
      // Poi i discendenti (per proprietà non in PREFER_INNER)
      if (!PREFER_INNER.has(p)) {
        for (const node of styledNodes) {
          const v = styleOf(node, p);
          if (!ABSTRACT.has(v)) return v;
        }
      }
      return concreteInheritedStyle(el, p);
    };

    fontFamily = normalizeFont(readConcrete("font-family"));
    fontSize = snapFont(readConcrete("font-size"));
    // I browser serializzano i colori come rgb() — convertiamo in hex
    color = normalizeColor(readConcrete("color")) || "#000000";

    // text-align: salta anche i placeholder browser (start/end) oltre a inherit
    const ALIGN_SKIP = new Set([
      "start",
      "end",
      "inherit",
      "initial",
      "unset",
      "",
    ]);
    const rawAlign = (() => {
      const direct = styleOf(el, "text-align");
      if (!ALIGN_SKIP.has(direct)) return direct;
      const INLINE_ALIGN_SKIP = new Set([
        "SPAN",
        "STRONG",
        "EM",
        "B",
        "I",
        "U",
        "A",
      ]);
      for (const node of styledNodes) {
        if (INLINE_ALIGN_SKIP.has(node.tagName)) continue;
        const v = styleOf(node, "text-align");
        if (!ALIGN_SKIP.has(v)) return v;
      }
      return concreteInheritedStyle(el, "text-align");
    })();
    textAlign = rawAlign || "left";

    rawLineHeight = readConcrete("line-height");

    fontWeight = readConcrete("font-weight") || "normal";
    fontStyle = readConcrete("font-style") || "normal";

    if (["400", "300"].includes(fontWeight)) fontWeight = "normal";
    if (["700", "600"].includes(fontWeight)) fontWeight = "bold";
    if (el.querySelector("b, strong") || ["B", "STRONG"].includes(el.tagName))
      fontWeight = "bold";
    if (el.querySelector("i, em") || ["I", "EM"].includes(el.tagName))
      fontStyle = "italic";
    if (el.querySelector("u") || el.tagName === "U")
      textDecoration = "underline";
  }

  // ── Padding ─────────────────────────────────────────────────────────────
  let padTop = "8px",
    padBottom = "8px",
    padLeft = "25px",
    padRight = "25px";

  if (isMjml) {
    const { paddingTd } = resolveContentDiv(el);
    if (paddingTd) {
      let pad = elementPadding(paddingTd);
      // If paddingTd has no padding, climb up to find the real section TD
      const hasRealPad = pad.top || pad.bottom || pad.left || pad.right;
      if (!hasRealPad) {
        let ancestor = paddingTd.parentElement;
        while (ancestor && ancestor.tagName !== "BODY") {
          if (ancestor.tagName === "TD") {
            const ap = elementPadding(ancestor as HTMLElement);
            if (ap.top || ap.bottom || ap.left || ap.right) {
              pad = ap;
              break;
            }
          }
          ancestor = ancestor.parentElement;
        }
      }
      padTop = snapPad(pad.top, "8px");
      padBottom = snapPad(pad.bottom, "8px");
      padLeft = snapPad(pad.left, "25px");
      padRight = snapPad(pad.right, "25px");
    }
  } else {
    // SunEditor copia padding:0px dal computed style su ogni elemento → non è padding
    // intenzionale. Cerchiamo il primo valore "reale" (non-zero) sul TD antenato.
    // Se non troviamo nulla, lasciamo pad vuoto così snapPad usa i default (25px L/R).
    const hasRealPad = (v: string) =>
      !!v && v !== "0" && v !== "0px" && v !== "0em" && v !== "0rem";

    const elPad = elementPadding(el);
    let pad = { top: "", bottom: "", left: "", right: "" };

    if (hasRealPad(elPad.left) || hasRealPad(elPad.right)) {
      pad = elPad;
    } else {
      let ancestor = el.parentElement;
      while (ancestor && ancestor.tagName !== "BODY") {
        if (ancestor.tagName === "TD") {
          const ap = elementPadding(ancestor as HTMLElement);
          if (hasRealPad(ap.left) || hasRealPad(ap.right)) {
            pad.left = ap.left;
            pad.right = ap.right;
            break;
          }
        }
        ancestor = ancestor.parentElement;
      }
      // Se il top/bottom viene dall'elemento e sono reali, recuperali
      if (!pad.top && hasRealPad(elPad.top)) pad.top = elPad.top;
      if (!pad.bottom && hasRealPad(elPad.bottom)) pad.bottom = elPad.bottom;
    }

    // Per WYSIWYG (SunEditor): il padding orizzontale è 0 di default (i template
    // SunEditor non hanno padding sulle TD — aggiungerlo creerebbe margini assenti
    // nell'originale). Il padding verticale rimane 8px per dare respiro visivo tra
    // i componenti nel layout Puck (l'originale usava <br> e margin dei paragrafi).
    padTop = snapPad(pad.top, "8px");
    padBottom = snapPad(pad.bottom, "8px");
    padLeft = snapPad(pad.left, "0px");
    padRight = snapPad(pad.right, "0px");
  }

  return {
    fontFamily,
    fontSize,
    lineHeight: snapLineHeight(rawLineHeight, parseFloat(fontSize), ""),
    color,
    fontWeight,
    fontStyle,
    textDecoration,
    textAlign,
    paddingTop: padTop,
    paddingBottom: padBottom,
    paddingLeft: padLeft,
    paddingRight: padRight,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Rich-content HTML cleaner
// ─────────────────────────────────────────────────────────────────────────────

const STRIP_OUTER_PROPS = new Set([
  "font-family",
  "font-size",
  "color",
  "text-align",
  "line-height",
  // Background: gestito a livello di componente Puck, non nel rich-text inline
  "background-color",
  "background",
]);

const STRIP_LAYOUT_PROPS = new Set([
  "font-size",
  "font-family",
  "line-height",
  "text-align",
  "word-break",
  "display",
  "direction",
  "vertical-align",
  "background-color",
  "background",
  "white-space",
  "orphans",
  "widows",
  "word-spacing",
  "-webkit-text-stroke-width",
  "box-sizing",
  "-webkit-user-drag",
  "overflow",
  "text-indent",
  "text-decoration-thickness",
  "text-decoration-color",
  "float",
]);

const cleanInnerHtml = (el: HTMLElement, isMjml: boolean): string => {
  const { contentDiv } = isMjml ? resolveContentDiv(el) : { contentDiv: null };
  const source = contentDiv ?? el;

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<body>${source.innerHTML}</body>`,
    "text/html",
  );

  const allNodes = Array.from(doc.body.querySelectorAll<HTMLElement>("*"));

  allNodes.forEach((node, idx) => {
    const style = node.getAttribute("style");
    if (style) {
      // Determine which props to strip:
      // - Elementi strutturali (div, p, td...) come primo figlio → strip props
      //   a livello di componente (font-size, color, ecc.) perché gestiti dalla sidebar Puck
      // - Elementi formattanti inline (strong, em, span, a...) come primo figlio →
      //   NON strippare color/font-weight perché sono stili inline intenzionali
      //   (es. <strong style="color:#558535"> deve restare verde in Quill)
      const STRUCTURAL_TAGS = new Set([
        "DIV",
        "P",
        "TD",
        "TH",
        "SECTION",
        "ARTICLE",
        "BLOCKQUOTE",
        "LI",
        "UL",
        "OL",
      ]);
      const isOutermostChild = idx === 0;
      const isStructural = STRUCTURAL_TAGS.has(node.tagName.toUpperCase());
      const stripSet =
        isOutermostChild && isStructural
          ? STRIP_OUTER_PROPS
          : STRIP_LAYOUT_PROPS;

      const kept = style
        .split(";")
        .map((s) => s.trim())
        .filter((s) => {
          if (!s) return false;
          const prop = s.split(":")[0].trim().toLowerCase();
          if (stripSet.has(prop)) return false;
          // Strip prefissi browser/Word artefatti (mso-*, -webkit-user-*, ecc.)
          if (
            prop.startsWith("mso-") ||
            prop.startsWith("-webkit-user-") ||
            prop.startsWith("-webkit-text-stroke") ||
            prop.startsWith("font-variant-") ||
            prop === "orphans" ||
            prop === "widows"
          )
            return false;
          // "text-transform: none" e "word-spacing: 0px" sono valori di default, rumore puro
          if (prop === "text-transform") {
            const val = s.split(":").slice(1).join(":").trim().toLowerCase();
            if (val === "none") return false;
          }
          // "color: inherit/initial/unset"
          if (prop === "color") {
            const val = s.split(":").slice(1).join(":").trim().toLowerCase();
            if (!val || ABSTRACT_CSS.has(val)) return false;
          }
          return true;
        })
        .join("; ");
      // Converti rgb()/rgba() → hex nei valori di stile rimasti
      if (kept) node.setAttribute("style", normalizeColorInStyle(kept));
      else node.removeAttribute("style");
    }
    node.removeAttribute("class");
    // Rimuovi attributi Word/Office (lang, valign, nowrap, ecc.)
    Array.from(node.attributes).forEach((attr) => {
      if (
        attr.name.startsWith("mso-") ||
        [
          "lang",
          "valign",
          "nowrap",
          "cellpadding",
          "cellspacing",
          "border",
        ].includes(attr.name)
      )
        node.removeAttribute(attr.name);
    });
  });

  // Remove "fake bold"
  const isNonBoldWeight = (v: string): boolean => {
    const w = v.trim().toLowerCase();
    return (
      w === "normal" ||
      w === "lighter" ||
      ["100", "200", "300", "400", "500"].includes(w)
    );
  };
  doc.body.querySelectorAll("b, strong").forEach((el) => {
    const hasOwnText = Array.from(el.childNodes).some(
      (n) =>
        n.nodeType === 3 &&
        (n.textContent || "").replace(/[\s​‌‍﻿]/g, "").length > 0,
    );
    if (hasOwnText) return;
    const overriddenToNormal = Array.from(
      el.querySelectorAll<HTMLElement>("[style]"),
    ).some((d) => isNonBoldWeight(styleOf(d, "font-weight")));
    if (!overriddenToNormal) return;
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });

  // Unwrap a single <p style="margin:0"> — MJML's default text wrapper
  const children = Array.from(doc.body.children);
  if (children.length === 1 && children[0].tagName === "P") {
    const p = children[0] as HTMLElement;
    const ps = (p.getAttribute("style") || "").replace(/\s/g, "");
    if (ps === "" || ps === "margin:0" || ps === "margin:0px") {
      return p.innerHTML;
    }
  }

  return doc.body.innerHTML;
};

const isBlockAnchor = (a: HTMLElement): boolean => {
  const bg = styleOf(a, "background-color") || styleOf(a, "background");
  if (!bg || bg === "transparent" || bg === "rgba(0, 0, 0, 0)") return false;
  const display = styleOf(a, "display");
  if (display === "block" || display === "inline-block") return true;
  const pad = styleOf(a, "padding");
  if (pad)
    return pad
      .split(/\s+/)
      .map((v) => parseInt(v) || 0)
      .some((v) => v >= 8);
  return false;
};

// ─────────────────────────────────────────────────────────────────────────────
// Block classifier
// ─────────────────────────────────────────────────────────────────────────────

interface PuckItem {
  type: string;
  props: Record<string, any>;
}

const classifyBlock = (el: HTMLElement, isMjml: boolean): PuckItem | null => {
  const tag = el.tagName?.toUpperCase() ?? "";
  const text = el.textContent?.trim() ?? "";
  if (!tag) return null;

  // ── MJML Divider: <td> containing a <p style="border-top:..."> with no text ───
  // MJML renders mj-divider as: <td padding><p style="border-top: X Y #color; font-size:1px"></p></td>
  //
  // KEY: browsers expand CSS shorthands when parsing style attributes.
  // "border-top: dotted 4px #000000" → border-top-width / border-top-style / border-top-color
  // We MUST read via styleOf() (el.style.getPropertyValue), NOT via regex on getAttribute("style").
  if (isMjml) {
    const isDividerP = (p: HTMLElement): boolean => {
      if (p.textContent?.trim()) return false;
      return (
        !!styleOf(p, "border-top-width") ||
        !!styleOf(p, "border-top-style") ||
        !!styleOf(p, "border-top-color") ||
        (p.getAttribute("style") || "").includes("border-top")
      );
    };

    const dividerP: HTMLElement | null = (() => {
      if ((tag === "TD" || tag === "TH") && !text) {
        const p = el.querySelector<HTMLElement>("p");
        if (p && isDividerP(p)) return p;
      }
      if (tag === "P" && isDividerP(el)) return el;
      return null;
    })();

    if (dividerP) {
      // Read individual long-hand properties (browsers expand the shorthand)
      const borderWidth =
        toPx(
          styleOf(dividerP, "border-top-width") ||
            styleOf(dividerP, "border-width") ||
            "1px",
        ) || "1px";

      const borderStyle =
        styleOf(dividerP, "border-top-style") ||
        styleOf(dividerP, "border-style") ||
        "solid";

      // Color may come as rgb() from browser normalization → convert to hex
      const rawColor =
        styleOf(dividerP, "border-top-color") ||
        styleOf(dividerP, "border-color") ||
        "#cccccc";
      const borderColor = normalizeColor(rawColor);

      // Padding: from the TD that wraps the <p>
      const padEl =
        tag === "TD" || tag === "TH"
          ? el
          : (dividerP.parentElement as HTMLElement | null);
      let pad = padEl
        ? elementPadding(padEl)
        : { top: "", bottom: "", left: "", right: "" };
      if (!pad.top && !pad.bottom && padEl) {
        let ancestor = padEl.parentElement;
        while (ancestor && ancestor.tagName !== "BODY") {
          if (ancestor.tagName === "TD") {
            const ap = elementPadding(ancestor as HTMLElement);
            if (ap.top || ap.bottom) {
              pad = ap;
              break;
            }
          }
          ancestor = ancestor.parentElement;
        }
      }

      return {
        type: "Divider",
        props: {
          id: uid("div"),
          borderColor,
          borderWidth,
          borderStyle,
          paddingTop: snapPad(pad.top, "10px"),
          paddingBottom: snapPad(pad.bottom, "10px"),
          paddingLeft: snapPad(pad.left, "25px"),
          paddingRight: snapPad(pad.right, "25px"),
        },
      };
    }
  }

  // ── Spacer detection ─────────────────────────────────────────────────────
  const heightAttr = styleOf(el, "height") || el.getAttribute("height") || "";
  const hasPadV =
    styleOf(el, "padding-top") ||
    styleOf(el, "padding-bottom") ||
    styleOf(el, "padding");
  const isSpacer =
    !text &&
    !el.querySelector("img") &&
    !el.querySelector("a") &&
    tag !== "HR" &&
    (!!heightAttr || !!hasPadV);
  if (
    !text &&
    !el.querySelector("img") &&
    !el.querySelector("a") &&
    tag !== "HR" &&
    !isSpacer
  )
    return null;

  // ── HR / Divider / Spacer ────────────────────────────────────────────────
  if (tag === "HR" || isSpacer) {
    let padTop = toPx(styleOf(el, "padding-top")) || "10px";
    let padBot = toPx(styleOf(el, "padding-bottom")) || "10px";
    if (isSpacer && heightAttr) {
      const h = parseInt(toPx(heightAttr));
      if (!isNaN(h) && h > 0) {
        padTop = `${Math.floor(h / 2)}px`;
        padBot = `${Math.ceil(h / 2)}px`;
      }
    }
    return {
      type: "Divider",
      props: {
        id: uid("div"),
        borderColor:
          tag === "HR"
            ? styleOf(el, "border-color") ||
              styleOf(el, "border-top-color") ||
              "#cccccc"
            : "transparent",
        borderWidth:
          tag === "HR"
            ? toPx(
                styleOf(el, "border-width") || styleOf(el, "border-top-width"),
              ) || "1px"
            : "0px",
        borderStyle:
          tag === "HR"
            ? styleOf(el, "border-style") ||
              styleOf(el, "border-top-style") ||
              "solid"
            : "solid",
        paddingTop: padTop,
        paddingBottom: padBot,
        paddingLeft: "25px",
        paddingRight: "25px",
      },
    };
  }

  // ── Full-width image (HeroImage) ─────────────────────────────────────────
  const imgs = el.querySelectorAll("img");
  if (imgs.length === 1 && !text.replace(/\s/g, "")) {
    const src = (imgs[0] as HTMLImageElement).getAttribute("src") || "";
    if (src) return { type: "HeroImage", props: { id: uid("img"), url: src } };
  }

  // ── ImageWithText ────────────────────────────────────────────────────────
  // MJML: section TD with two mj-column-per-* divs as direct children
  // Plain: TR with two content TDs
  const mjCols = Array.from(el.children).filter(
    (ch) =>
      ch.tagName === "DIV" &&
      (ch.getAttribute("class") || "").includes("mj-column-per-"),
  ) as HTMLElement[];
  const trCols =
    !mjCols.length && tag === "TR"
      ? (Array.from(el.children).filter(
          (ch) => ch.tagName === "TD" || ch.tagName === "TH",
        ) as HTMLElement[])
      : [];
  const cols: HTMLElement[] =
    mjCols.length >= 2 ? mjCols : trCols.length >= 2 ? trCols : [];

  if (cols.length >= 2) {
    let imgCell: HTMLElement | null = null;
    let txtCell: HTMLElement | null = null;
    for (const cell of cols) {
      if (!imgCell && cell.querySelector("img")) imgCell = cell;
      else if (!txtCell && (cell.textContent?.trim() || "").length > 0)
        txtCell = cell;
    }
    if (imgCell && txtCell) {
      const imgEl = imgCell.querySelector("img") as HTMLImageElement;
      const styling = extractProps(txtCell, isMjml);
      const txtHtml = cleanInnerHtml(txtCell, isMjml);
      let imageWidth = "40%";
      const m = imgCell.className?.match(/mj-column-per-(\d+)/);
      if (m) imageWidth = `${m[1]}%`;
      return {
        type: "ImageWithText",
        props: {
          id: uid("imgtxt"),
          imageUrl: imgEl?.getAttribute("src") || "",
          imageAlt: imgEl?.getAttribute("alt") || "",
          imageWidth,
          text: txtHtml,
          layout:
            cols.indexOf(imgCell) < cols.indexOf(txtCell)
              ? "image-left"
              : "image-right",
          ...styling,
        },
      };
    }
  }

  // ── CTA ──────────────────────────────────────────────────────────────────
  // MJML button pattern:
  //   <table style="border-collapse:separate"> → <td bgcolor="#xxx" style="background:#xxx; border-radius:...">
  //     <p href style="padding:10px 25px; color:#fff; ...">  ← style source
  //     <p>label</p>
  //
  // We find the innermost TD with a solid background color.
  const findCtaTd = (root: HTMLElement): HTMLElement | null => {
    const rootBg =
      styleOf(root, "background-color") ||
      styleOf(root, "background") ||
      root.getAttribute("bgcolor") ||
      "";
    if (
      rootBg &&
      rootBg !== "transparent" &&
      rootBg !== "rgba(0, 0, 0, 0)" &&
      root.tagName === "TD"
    )
      return root;
    const candidates = Array.from(
      root.querySelectorAll<HTMLElement>("td[bgcolor], td[style]"),
    );
    for (const td of candidates) {
      const bg =
        styleOf(td, "background-color") ||
        styleOf(td, "background") ||
        td.getAttribute("bgcolor") ||
        "";
      if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)") return td;
    }
    return null;
  };

  const ctaTd = findCtaTd(el);
  if (ctaTd && text.length > 0 && text.length < 80) {
    // styleSource = the <p href> or <a> that carries font/color/padding for the button
    const styleSource =
      ctaTd.querySelector<HTMLElement>("a, p[href], p[style]") ?? ctaTd;
    const bgColor =
      styleOf(ctaTd, "background-color") ||
      styleOf(ctaTd, "background") ||
      ctaTd.getAttribute("bgcolor") ||
      "#414141";
    const textColor = styleOf(styleSource, "color") || "#ffffff";
    const borderRadius =
      styleOf(ctaTd, "border-radius") ||
      styleOf(styleSource, "border-radius") ||
      "0px";
    const href =
      (styleSource as HTMLAnchorElement).href ||
      styleSource.getAttribute("href") ||
      "";
    const padSrc = elementPadding(styleSource);
    const paddingV =
      padSrc.top || toPx(styleOf(ctaTd, "padding-top")) || "10px";
    const paddingH =
      padSrc.left || toPx(styleOf(ctaTd, "padding-left")) || "25px";

    // Read font props directly from styleSource (where MJML puts them)
    const ctaFontFamily = normalizeFont(
      styleOf(styleSource, "font-family") || DEFAULT_FONT,
    );
    const ctaFontSize = snapFont(styleOf(styleSource, "font-size") || "15px");
    const ctaFontWeight = (() => {
      const fw = styleOf(styleSource, "font-weight") || "normal";
      if (["400", "300", "200", "100"].includes(fw)) return "normal";
      if (["700", "600", "800", "900", "bold"].includes(fw)) return "bold";
      return fw;
    })();

    // Section-level padding (the wrapping TD that contains the button table)
    let sectionPadTop = "8px",
      sectionPadBottom = "8px",
      sectionPadLeft = "25px",
      sectionPadRight = "25px";
    let ancestor: HTMLElement | null =
      ctaTd.parentElement as HTMLElement | null;
    while (ancestor && ancestor.tagName !== "BODY") {
      if (ancestor.tagName === "TD") {
        const ap = elementPadding(ancestor);
        if (ap.top || ap.bottom || ap.left || ap.right) {
          sectionPadTop = snapPad(ap.top, "8px");
          sectionPadBottom = snapPad(ap.bottom, "8px");
          sectionPadLeft = snapPad(ap.left, "25px");
          sectionPadRight = snapPad(ap.right, "25px");
          break;
        }
      }
      ancestor = ancestor.parentElement as HTMLElement | null;
    }

    let align = "center";
    let alignAncestor = ctaTd.parentElement as HTMLElement | null;
    while (alignAncestor && alignAncestor.tagName !== "BODY") {
      const a =
        styleOf(alignAncestor, "text-align") ||
        alignAncestor.getAttribute("align") ||
        "";
      if (a) {
        align = a;
        break;
      }
      alignAncestor = alignAncestor.parentElement as HTMLElement | null;
    }

    return {
      type: "CTA",
      props: {
        id: uid("cta"),
        label: text,
        url: href,
        bgColor,
        textColor,
        borderRadius,
        align,
        paddingVertical: snapPad(paddingV, "10px"),
        paddingHorizontal: snapPad(paddingH, "25px"),
        fontFamily: ctaFontFamily,
        fontSize: ctaFontSize,
        fontWeight: ctaFontWeight,
        fontStyle: styleOf(styleSource, "font-style") || "normal",
        textDecoration: "none",
        color: textColor,
        textAlign: align,
        paddingTop: sectionPadTop,
        paddingBottom: sectionPadBottom,
        paddingLeft: sectionPadLeft,
        paddingRight: sectionPadRight,
      },
    };
  }

  // ── Hyperlink (single inline link in text) ────────────────────────────────
  const anchors = Array.from(el.querySelectorAll<HTMLAnchorElement>("a"));
  if (anchors.length === 1 && text.length > 0 && !isBlockAnchor(anchors[0])) {
    const a = anchors[0];
    const fullText = el.textContent || "";
    const linkText = a.textContent?.trim() || "";
    const linkIdx = fullText.indexOf(linkText);
    const preText = linkIdx > 0 ? fullText.substring(0, linkIdx).trim() : "";
    // Se c'è testo significativo DOPO il link (es. "...Le commenteremo durante la diretta!")
    // il componente Hyperlink non può contenerlo (ha solo preText, non postText).
    // In quel caso classifichiamo come Text: il link <a> resta nel rich-text di Quill.
    const afterIdx = linkIdx >= 0 ? linkIdx + linkText.length : fullText.length;
    const postText = visibleText(fullText.substring(afterIdx));
    if (postText) {
      // Fall through to Text classification below
    } else {
      return {
        type: "Hyperlink",
        props: {
          id: uid("link"),
          preText,
          linkLabel: linkText,
          url: a.href || "",
          linkColor:
            normalizeColor(styleOf(a, "color") || inheritedStyle(a, "color")) ||
            "#0066cc",
          ...extractProps(el, isMjml),
        },
      };
    }
  }

  // ── Resolve effective font size for heading detection ─────────────────────
  let effectiveFontSize = 0;
  if (isMjml) {
    const { contentDiv } = resolveContentDiv(el);
    effectiveFontSize =
      parseFloat(toPx(styleOf(contentDiv ?? el, "font-size"))) || 0;
  } else {
    const directFs = styleOf(el, "font-size");
    effectiveFontSize = parseFloat(toPx(directFs)) || 0;
    if (effectiveFontSize === 0) {
      // SunEditor mette font-size: inherit sugli elementi intermedi e il
      // valore concreto (es. 16pt) solo sullo span più interno.
      // Scandiamo tutti i discendenti [style] per trovare il primo valore reale.
      const ABSTRACT_FS = new Set(["inherit", "initial", "unset", ""]);
      for (const node of Array.from(
        el.querySelectorAll<HTMLElement>("[style]"),
      )) {
        const fs = styleOf(node, "font-size");
        if (!ABSTRACT_FS.has(fs)) {
          effectiveFontSize = parseFloat(toPx(fs)) || 0;
          if (effectiveFontSize > 0) break;
        }
      }
    }
    // Fallback: risali ai padri
    if (effectiveFontSize === 0)
      effectiveFontSize =
        parseFloat(toPx(concreteInheritedStyle(el, "font-size"))) || 0;
  }

  // ── Title (H1 or >= 28px) ────────────────────────────────────────────────
  if (tag === "H1" || effectiveFontSize >= 28) {
    const styling = extractProps(el, isMjml);
    return {
      type: "Title",
      props: {
        id: uid("title"),
        text: cleanInnerHtml(el, isMjml),
        ...styling,
        textAlign: styling.textAlign || "center",
      },
    };
  }

  // ── Subtitle (H2/H3 or 18–27px) ─────────────────────────────────────────
  if (
    tag === "H2" ||
    tag === "H3" ||
    (effectiveFontSize >= 18 && effectiveFontSize < 28)
  ) {
    const styling = extractProps(el, isMjml);
    return {
      type: "Subtitle",
      props: { id: uid("sub"), text: cleanInnerHtml(el, isMjml), ...styling },
    };
  }

  // ── Generic Text ─────────────────────────────────────────────────────────
  const styling = extractProps(el, isMjml);
  return {
    type: "Text",
    props: { id: uid("text"), text: cleanInnerHtml(el, isMjml), ...styling },
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Template format detector
// ─────────────────────────────────────────────────────────────────────────────

export const isMjmlTemplate = (doc: Document): boolean =>
  !!doc.querySelector('[aria-roledescription="email"]') ||
  !!doc.querySelector('[class*="mj-column-per-"]');

// ─────────────────────────────────────────────────────────────────────────────
// MJML section extractor
// ─────────────────────────────────────────────────────────────────────────────

const extractMjmlSections = (emailBody: HTMLElement): HTMLElement[] => {
  const sectionTds: HTMLElement[] = [];

  const collect = (el: HTMLElement) => {
    if (!el.tagName) return;
    const tag = el.tagName.toUpperCase();

    if (tag === "TD" || tag === "TH") {
      const colDivs = Array.from(el.children).filter(
        (ch) =>
          ch.tagName === "DIV" &&
          (ch.getAttribute("class") || "").includes("mj-column-per-"),
      );
      if (colDivs.length > 0) {
        sectionTds.push(el);
        return; // don't recurse inside this TD
      }
    }

    if (
      ["DIV", "TABLE", "TBODY", "THEAD", "TR", "TD", "TH", "SECTION"].includes(
        tag,
      )
    ) {
      Array.from(el.children).forEach((ch) => collect(ch as HTMLElement));
    }
  };

  collect(emailBody);
  return sectionTds;
};

const sectionToBlocks = (sectionTd: HTMLElement): HTMLElement[] => {
  const colDivs = Array.from(sectionTd.children).filter(
    (ch) =>
      ch.tagName === "DIV" &&
      (ch.getAttribute("class") || "").includes("mj-column-per-"),
  ) as HTMLElement[];

  // Multi-column → single ImageWithText candidate (pass sectionTd, classifier handles it)
  if (colDivs.length >= 2) return [sectionTd];

  // Single column: walk down to find the leaf "content TDs"
  // A content TD is a TD whose direct children include:
  //   - a div carrying font-family (the MJML content div)
  //   - direct block-level content (P, H1, H2, IMG, etc.)
  //   - a button/CTA table (td with bgcolor/background style)
  const contentTds: HTMLElement[] = [];

  const findLeaf = (el: HTMLElement) => {
    if (!el.tagName) return;
    const tag = el.tagName.toUpperCase();

    if (tag === "TD" || tag === "TH") {
      const children = Array.from(el.children) as HTMLElement[];

      // Has a content div (div with font-family on inline style)
      const hasContentDiv = children.some(
        (ch) =>
          ch.tagName === "DIV" &&
          (ch.getAttribute("style") || "").includes("font-family"),
      );

      // Has direct block-level tags
      const hasDirect = children.some((ch) =>
        [
          "P",
          "H1",
          "H2",
          "H3",
          "H4",
          "HR",
          "IMG",
          "UL",
          "OL",
          "BLOCKQUOTE",
        ].includes(ch.tagName.toUpperCase()),
      );

      // This TD itself is a CTA button cell (has bgcolor attr or background in its own style)
      const ownStyle = el.getAttribute("style") || "";
      const isCtaCell =
        !!el.getAttribute("bgcolor") ||
        (ownStyle.includes("background") &&
          !ownStyle.includes("background:transparent") &&
          !ownStyle.includes("background: transparent"));

      if (hasContentDiv || hasDirect || isCtaCell) {
        contentTds.push(el);
        return;
      }
    }

    Array.from(el.children).forEach((ch) => findLeaf(ch as HTMLElement));
  };

  colDivs.forEach((col) => findLeaf(col));

  if (contentTds.length > 0) return contentTds;

  // Fallback: the sectionTd itself
  return [sectionTd];
};

// ─────────────────────────────────────────────────────────────────────────────
// WYSIWYG block extractor
// ─────────────────────────────────────────────────────────────────────────────

const findWysiwygBody = (doc: Document): HTMLElement => {
  for (const t of Array.from(doc.querySelectorAll<HTMLTableElement>("table"))) {
    const w = t.getAttribute("width");
    if (w && !w.includes("%") && parseInt(w) >= 400 && parseInt(w) <= 900)
      return t;
  }
  for (const d of Array.from(doc.querySelectorAll<HTMLElement>("div"))) {
    const mw = styleOf(d, "max-width");
    if (mw?.includes("px") && parseInt(mw) >= 400 && parseInt(mw) <= 900)
      return d;
  }
  return doc.body;
};

const extractWysiwygBlocks = (container: HTMLElement): HTMLElement[] => {
  const blocks: HTMLElement[] = [];
  const visit = (el: HTMLElement) => {
    const tag = (el.tagName || "").toUpperCase();
    // visibleText rimuove zero-width space, NBSP e altri caratteri invisibili
    // usati come placeholder dai WYSIWYG editor per non creare componenti "vuoti"
    const text = visibleText(el.textContent || "");
    const hasImg = !!el.querySelector("img");
    if (!text && !hasImg && tag !== "HR") return;

    if (["TABLE", "TBODY", "THEAD", "TR"].includes(tag)) {
      Array.from(el.children).forEach((ch) => visit(ch as HTMLElement));
      return;
    }
    if (tag === "TD" || tag === "TH") {
      if (
        el.querySelectorAll("img").length === 1 &&
        !el.textContent?.replace(/\s/g, "")
      ) {
        blocks.push(el);
        return;
      }
      Array.from(el.children).forEach((ch) => visit(ch as HTMLElement));
      return;
    }
    if (tag === "DIV") {
      const cls = el.className || "";
      if (cls.includes("se-image-container") || cls.includes("se-component")) {
        blocks.push(el);
        return;
      }
      const children = Array.from(el.children) as HTMLElement[];
      if (children.length === 1) {
        const INLINE_TAGS = new Set([
          "SPAN",
          "STRONG",
          "EM",
          "B",
          "I",
          "U",
          "A",
        ]);
        const onlyChildTag = children[0].tagName.toUpperCase();
        if (INLINE_TAGS.has(onlyChildTag) || onlyChildTag === "BR") {
          if (text || hasImg) blocks.push(el);
          return;
        }
        visit(children[0]);
        return;
      }
      // Se tutti i figli diretti sono tag block-level, questo DIV è un wrapper
      // "contenitore puro": ricorrere trasparentemente così ogni figlio diventa
      // il proprio componente Puck invece di collassare tutto in uno solo.
      // (Se il DIV ha contenuto inline misto o figli SPAN/A resta un blocco unico.)
      const BLOCK_TAGS = new Set([
        "DIV",
        "P",
        "H1",
        "H2",
        "H3",
        "H4",
        "H5",
        "H6",
        "HR",
        "UL",
        "OL",
        "BLOCKQUOTE",
      ]);
      if (
        children.length > 0 &&
        children.every((ch) =>
          BLOCK_TAGS.has((ch as HTMLElement).tagName.toUpperCase()),
        )
      ) {
        children.forEach((ch) => visit(ch as HTMLElement));
        return;
      }
      if (text || hasImg) blocks.push(el);
      return;
    }
    if (
      [
        "H1",
        "H2",
        "H3",
        "H4",
        "P",
        "HR",
        "IMG",
        "A",
        "SPAN",
        "BLOCKQUOTE",
        "UL",
        "OL",
      ].includes(tag)
    ) {
      blocks.push(el);
      return;
    }
    if (text || hasImg) blocks.push(el);
  };
  Array.from(container.children).forEach((ch) => visit(ch as HTMLElement));
  return blocks;
};

// ─────────────────────────────────────────────────────────────────────────────
// Root props
// ─────────────────────────────────────────────────────────────────────────────

const VALID_EMAIL_WIDTHS = [500, 600, 700, 800];

const snapWidth = (raw: string): string => {
  const px = parseInt(raw);
  if (isNaN(px)) return "600px";
  const closest = VALID_EMAIL_WIDTHS.reduce((p, c) =>
    Math.abs(c - px) < Math.abs(p - px) ? c : p,
  );
  return `${closest}px`;
};

const extractRootProps = (doc: Document) => {
  const bodyBg = styleOf(doc.body, "background-color");
  const bgColor =
    !bodyBg || bodyBg === "transparent" || bodyBg === "rgba(0, 0, 0, 0)"
      ? "#ffffff"
      : bodyBg;

  // Collect all candidate width values from tables and divs
  let rawWidth = "600";
  doc.querySelectorAll("table").forEach((t) => {
    const w = t.getAttribute("width");
    if (w && !w.includes("%")) {
      const px = parseInt(w);
      if (px >= 400 && px <= 900) rawWidth = String(px);
    }
    const mw = styleOf(t, "max-width");
    if (mw?.includes("px")) {
      const px = parseInt(mw);
      if (px >= 400 && px <= 900) rawWidth = String(px);
    }
  });
  doc.querySelectorAll<HTMLElement>("div").forEach((d) => {
    const mw = styleOf(d, "max-width");
    if (mw?.includes("px")) {
      const px = parseInt(mw);
      if (px >= 400 && px <= 900) rawWidth = String(px);
    }
  });

  const rootWidth = snapWidth(rawWidth);

  let bodyBgColor = "#ffffff";
  const iw =
    doc.querySelector<HTMLElement>(".inner-wrapper") ||
    doc.querySelector<HTMLElement>('[aria-roledescription="email"]');
  if (iw) {
    const bg = styleOf(iw, "background-color");
    if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)")
      bodyBgColor = bg;
  }

  return { bgColor, bgImage: "", bodyBgColor, bodyBgImage: "", rootWidth };
};

const findMjmlBody = (doc: Document): HTMLElement => {
  const aria = doc.querySelector<HTMLElement>('[aria-roledescription="email"]');
  if (aria) return aria;
  let best: HTMLElement | null = null;
  doc.querySelectorAll("table").forEach((t) => {
    const w =
      t.getAttribute("width") || styleOf(t, "max-width") || styleOf(t, "width");
    if (w && ["600", "500", "700", "800"].some((x) => w.includes(x))) best = t;
  });
  return best ?? doc.body;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main entry point
// ─────────────────────────────────────────────────────────────────────────────

export function convertHtmlToPuckData(htmlString: string): Data<any, any> {
  resetIds();

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const rootProps = extractRootProps(doc);
  const mjml = isMjmlTemplate(doc);

  const puckItems: PuckItem[] = [];

  if (mjml) {
    const emailBody = findMjmlBody(doc);
    const sectionTds = extractMjmlSections(emailBody);
    for (const sectionTd of sectionTds) {
      for (const block of sectionToBlocks(sectionTd)) {
        const item = classifyBlock(block, true);
        if (item) puckItems.push(item);
      }
    }
  } else {
    const emailBody = findWysiwygBody(doc);
    for (const block of extractWysiwygBlocks(emailBody)) {
      const item = classifyBlock(block, false);
      if (item) puckItems.push(item);
    }
  }

  if (puckItems.length === 0) {
    const body = mjml ? findMjmlBody(doc) : findWysiwygBody(doc);
    puckItems.push({
      type: "Text",
      props: {
        id: uid("text"),
        text: body.innerHTML || "Contenuto importato vuoto",
        fontFamily: DEFAULT_FONT,
        fontSize: "15px",
        color: "#000000",
        fontWeight: "normal",
        fontStyle: "normal",
        textDecoration: "none",
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      },
    });
  }

  const result: Data<any, any> = {
    content: puckItems,
    root: { props: rootProps },
  };

  return result;
}
